import React from 'react';
import { ArrowRight, CheckCircle, XCircle, RocketIcon, SparklesIcon, WalletIcon, PaintBrushIcon, DownloadIcon } from './icons';
export interface Feature {
  icon: React.ReactNode;
  label: string;
  hasFeature: boolean;
  competitorHasFeatures: boolean[];
}
export interface FeatureComparisonProps {
  'data-id'?: string;
  badge?: string;
  title: string | React.ReactNode;
  subtitle: string;
  ctaText?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  brandName: string;
  competitorLabels: string[];
  features: Feature[];
  className?: string;
}
export const FeatureComparison: React.FC<FeatureComparisonProps> = ({
  'data-id': dataId,
  badge = 'Why we stand out',
  title,
  subtitle,
  ctaText = 'Get Started',
  ctaHref = '#',
  onCtaClick,
  brandName,
  competitorLabels,
  features,
  className = ''
}) => {
  const totalColumns = 1 + competitorLabels.length; // brand + competitors
  return <section className={`relative w-full bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-8 overflow-hidden ${className}`} data-id="element-78">
      {/* Subtle background decoration - responsive sizing */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" data-id="element-79">
        <div className="absolute top-0 left-1/4 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-[#3ab795] rounded-full blur-[80px] sm:blur-[100px] md:blur-[120px] opacity-10" data-id="element-80" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-[#3ab795] rounded-full blur-[80px] sm:blur-[100px] md:blur-[120px] opacity-10" data-id="element-81" />
      </div>

      <div className="relative max-w-7xl mx-auto z-10" data-id="element-82">
        <div className="grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-8 sm:gap-10 md:gap-12 lg:gap-16 items-start" data-id="element-83">
          {/* Left Content */}
          <div className="flex flex-col gap-6 sm:gap-8" data-id="element-84">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm w-fit" data-id="element-85">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-86" />
              <p className="text-xs sm:text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-87">
                {badge}
              </p>
            </div>

            {/* Title & Subtitle */}
            <div className="flex flex-col gap-4 sm:gap-6" data-id="element-88">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight" data-id="element-89">
                {title}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-[#b8bcc8] leading-relaxed" data-id="element-90">
                {subtitle}
              </p>
            </div>

            {/* CTA Button */}
            <a href={ctaHref} onClick={onCtaClick} className="group inline-flex items-center justify-center gap-2 sm:gap-3 bg-white hover:bg-gray-100 text-black font-semibold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 rounded-xl transition-all duration-200 w-full sm:w-fit shadow-lg" data-id="element-91">
              <span data-id="element-92">Join Beta</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 group-hover:translate-x-1" data-id="element-93" />
            </a>
          </div>

          {/* Right Comparison Card */}
          <div className="w-full overflow-x-auto -mx-4 sm:mx-0" data-id="element-94">
            <div className="min-w-[600px] sm:min-w-0 px-4 sm:px-0" data-id="element-95">
              <div className="bg-[#1a1a1a] border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm" data-id="element-96">
                {/* Header Row */}
                <div className="grid gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-5 bg-[#0f0f0f] border-b border-white/10" style={{
                gridTemplateColumns: `minmax(120px, 1fr) repeat(${totalColumns}, minmax(70px, auto))`
              }} data-id="element-97">
                  <div className="text-[10px] sm:text-xs font-semibold text-[#b8bcc8] uppercase tracking-wide" data-id="element-98">
                    Features
                  </div>
                  <div className="text-center" data-id="element-99">
                    <div className="inline-flex px-2 sm:px-2.5 py-1 sm:py-1.5 bg-[#3ab795] text-white text-[10px] sm:text-xs font-bold rounded-md sm:rounded-lg whitespace-nowrap" data-id="element-100">
                      {brandName}
                    </div>
                  </div>
                  {competitorLabels.map((label, index) => <div key={index} className="text-center" data-id="element-101">
                      <div className="inline-flex px-2 sm:px-2.5 py-1 sm:py-1.5 bg-white/5 text-[#b8bcc8] text-[10px] sm:text-xs font-semibold rounded-md sm:rounded-lg whitespace-nowrap" data-id="element-102">
                        {label}
                      </div>
                    </div>)}
                </div>

                {/* Feature Rows */}
                <div className="divide-y divide-white/5" data-id="element-103">
                  {features.map((feature, index) => <div key={index} className="grid gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4 hover:bg-white/5 transition-colors" style={{
                  gridTemplateColumns: `minmax(120px, 1fr) repeat(${totalColumns}, minmax(70px, auto))`
                }} data-id="element-104">
                      <div className="flex items-center gap-2 sm:gap-2.5" data-id="element-105">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 text-[#b8bcc8] flex-shrink-0" data-id="element-106">
                          {feature.icon}
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-white leading-tight" data-id="element-107">
                          {feature.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-center" data-id="element-108">
                        {feature.hasFeature ? <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#3ab795]/20 flex items-center justify-center" data-id="element-109">
                            <CheckCircle className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 text-[#3ab795]" data-id="element-110" />
                          </div> : <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/5 flex items-center justify-center" data-id="element-111">
                            <XCircle className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 text-white/40" data-id="element-112" />
                          </div>}
                      </div>
                      {feature.competitorHasFeatures.map((hasFeature, idx) => <div key={idx} className="flex items-center justify-center" data-id="element-113">
                          {hasFeature ? <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/5 flex items-center justify-center" data-id="element-114">
                              <CheckCircle className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 text-white/40" data-id="element-115" />
                            </div> : <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/5 flex items-center justify-center" data-id="element-116">
                              <XCircle className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 text-white/40" data-id="element-117" />
                            </div>}
                        </div>)}
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
// Default feature icons export for convenience
export const defaultFeatureIcons = {
  rocket: <RocketIcon className="w-full h-full fill-current" data-id="element-118" />,
  sparkles: <SparklesIcon className="w-full h-full fill-current" data-id="element-119" />,
  wallet: <WalletIcon className="w-full h-full fill-current" data-id="element-120" />,
  paintBrush: <PaintBrushIcon className="w-full h-full fill-current" data-id="element-121" />,
  download: <DownloadIcon className="w-full h-full fill-current" data-id="element-122" />
};